/**
 * Revlon Global PDP — product description editorial layout
 *
 * Product descriptions arrive as pre-built Bootstrap A+ HTML with
 * additional-product-info__* classes.  We walk the tree read-only,
 * detect section headings (h2 with -header class), group content, and
 * rebuild clean markup.
 *
 * CRITICAL: h2 headings carry class "additional-product-info__block".
 * They must NEVER be unwrapped — only their text would remain.
 */
(function () {

  var NAMED_SECTIONS = /^(DESCRIPTION|DETAILS|HOW\s+TO\s+USE(?:\s+IT)?|INGREDIENTS)$/i;

  function normalizeText(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  function isHeadingTag(tag) {
    return /^H[1-6]$/.test(tag);
  }

  function matchNamedTitle(text) {
    var t = normalizeText(text).toUpperCase();
    if (NAMED_SECTIONS.test(t)) {
      return t.replace(/HOW TO USE$/, 'HOW TO USE IT');
    }
    return null;
  }

  function getSectionSlug(title) {
    var u = (title || '').toUpperCase();
    if (u.indexOf('DESCRIPTION') !== -1) return 'description';
    if (u.indexOf('DETAILS')     !== -1) return 'details';
    if (u.indexOf('HOW')          !== -1) return 'how-to-use';
    if (u.indexOf('INGREDIENT')   !== -1) return 'ingredients';
    return 'generic';
  }

  function isEmptyParagraph(el) {
    if (!el || el.tagName !== 'P') return false;
    var html = el.innerHTML.replace(/&nbsp;/gi, ' ').replace(/<br\s*\/?>/gi, ' ');
    return normalizeText(html) === '';
  }

  function isHiddenElement(el) {
    if (!el || el.nodeType !== 1) return false;
    var style = el.getAttribute('style') || '';
    if (/display\s*:\s*none/i.test(style)) return true;
    return false;
  }

  function isMediaBlock(el) {
    if (!el || el.nodeType !== 1) return false;
    var tag = el.tagName;
    if (tag === 'IMG' || tag === 'VIDEO' || tag === 'PICTURE' || tag === 'IFRAME') return true;
    var mediaEl = el.querySelector('img, video, iframe, picture');
    return !!(mediaEl && normalizeText(el.textContent).length === 0);
  }

  function findMediaIndex(nodes) {
    for (var i = 0; i < nodes.length; i++) {
      if (isMediaBlock(nodes[i])) return i;
    }
    return -1;
  }

  function unwrapMedia(node) {
    var t = node.tagName;
    if (t === 'IMG' || t === 'VIDEO' || t === 'PICTURE' || t === 'IFRAME') {
      return node.cloneNode(true);
    }
    var found = node.querySelector('img, video, picture, iframe');
    return found ? found.cloneNode(true) : node.cloneNode(true);
  }

  function appendNodes(target, nodes) {
    nodes.forEach(function (node) { target.appendChild(node); });
  }

  function isSectionMarker(node) {
    return node.nodeType === 1 && node.dataset &&
      (node.dataset.namedSection === 'true' || node.dataset.arbitraryHeading === 'true');
  }

  /**
   * Layout shells to recurse into — never collected as content.
   * Headings (h1-h6) are NEVER wrappers even if they carry __block class.
   */
  function isLayoutWrapper(el) {
    if (!el || el.nodeType !== 1) return false;
    if (isHeadingTag(el.tagName)) return false;

    if (el.classList.contains('container') ||
        el.classList.contains('aplus') ||
        el.classList.contains('row') ||
        el.classList.contains('less-spacing-m') ||
        el.classList.contains('no-gutters')) {
      return true;
    }

    if (typeof el.className === 'string' &&
        /\bcol-(?:xs|sm|md|lg|xl)-\d+\b/.test(el.className)) {
      return true;
    }

    var shells = [
      'additional-product-info__section',
      'additional-product-info__split-layout',
      'additional-product-info__content-column',
      'additional-product-info__image-column',
      'additional-product-info__image',
      'additional-product-info__media-block',
      'additional-product-info__content',
      'additional-product-info__description-content',
      'additional-product-info__details-content',
      'additional-product-info__how-to-use-content',
      'additional-product-info__ingredients-content'
    ];

    for (var i = 0; i < shells.length; i++) {
      if (el.classList.contains(shells[i])) return true;
    }

    return false;
  }

  /**
   * Read-only walk: recurse into layout wrappers, emit heading markers
   * and content nodes.  Does NOT mutate the source DOM.
   */
  function collectFlatNodes(parent, result) {
    if (!result) result = [];

    Array.prototype.forEach.call(parent.childNodes, function (node) {
      if (node.nodeType === 3) {
        if (normalizeText(node.textContent)) {
          result.push(node.cloneNode());
        }
        return;
      }

      if (node.nodeType !== 1) return;

      var el = node;

      if (isHiddenElement(el)) return;
      if (isEmptyParagraph(el)) return;

      /* Skip empty layout shells (col with only <br>) */
      if (isLayoutWrapper(el) && !normalizeText(el.textContent) &&
          !el.querySelector('img, video, iframe')) {
        return;
      }

      /* ── Section heading → marker node (always h2) ── */
      if (isHeadingTag(el.tagName)) {
        var named = matchNamedTitle(el.textContent);
        if (named) {
          var h = document.createElement('h2');
          h.textContent = named;
          h.dataset.namedSection = 'true';
          result.push(h);
          return;
        }
        if (!normalizeText(el.textContent)) return;
        var hx = document.createElement(el.tagName.toLowerCase());
        hx.textContent = normalizeText(el.textContent);
        hx.dataset.arbitraryHeading = 'true';
        result.push(hx);
        return;
      }

      /* ── Layout shell → recurse, do not collect the shell itself ── */
      if (isLayoutWrapper(el)) {
        collectFlatNodes(el, result);
        return;
      }

      /* ── Leaf content node ── */
      result.push(el.cloneNode(true));
    });

    return result;
  }

  function flatNodesToSections(flatNodes) {
    var sections = [];
    var current  = null;

    flatNodes.forEach(function (node) {
      if (isSectionMarker(node)) {
        current = {
          title : normalizeText(node.textContent),
          nodes : []
        };
        sections.push(current);
        return;
      }

      if (!current) {
        current = { title: '', nodes: [] };
        sections.push(current);
      }
      current.nodes.push(node);
    });

    return sections;
  }

  function buildSequentialContent(parent, nodes, contentClass) {
    var textBuffer = [];

    function flushText() {
      if (!textBuffer.length) return;
      var wrap = document.createElement('div');
      wrap.className = contentClass;
      appendNodes(wrap, textBuffer);
      parent.appendChild(wrap);
      textBuffer = [];
    }

    nodes.forEach(function (node) {
      if (isMediaBlock(node)) {
        flushText();
        var media = document.createElement('div');
        media.className = 'additional-product-info__media-block';
        media.appendChild(unwrapMedia(node));
        parent.appendChild(media);
      } else {
        textBuffer.push(node);
      }
    });

    flushText();
  }

  function setupIngredientsToggle(block) {
    var content = block.querySelector('.additional-product-info__ingredients-content');
    if (!content) return;
    var plain = content.textContent || '';
    if (plain.length < 320) return;

    var fullHtml = content.innerHTML;
    var preview  = plain.slice(0, 280).trim() + '\u2026';
    content.dataset.fullHtml = fullHtml;
    content.textContent = preview;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'additional-product-info__ingredients-toggle';
    btn.textContent = 'FULL LIST';
    var expanded = false;

    btn.addEventListener('click', function () {
      expanded = !expanded;
      content.innerHTML = expanded ? content.dataset.fullHtml : preview;
      btn.textContent = expanded ? 'SHOW LESS' : 'FULL LIST';
    });

    block.appendChild(btn);
  }

  function buildSection(section) {
    var block = document.createElement('div');
    block.className = 'additional-product-info__block additional-product-info__section';

    var slug = getSectionSlug(section.title);

    if (section.title) {
      var header = document.createElement('h2');
      header.className =
        'additional-product-info__block-header' +
        ' additional-product-info__' + slug + '-header';
      header.textContent = section.title;
      block.appendChild(header);
    }

    var nodes = section.nodes.filter(function (n) {
      if (n.nodeType === 1 && isEmptyParagraph(n)) return false;
      if (n.nodeType === 1 && isSectionMarker(n)) return false;
      if (n.nodeType === 3) return normalizeText(n.textContent).length > 0;
      return true;
    });

    if (!nodes.length) return block;

    var contentClass =
      'additional-product-info__content' +
      ' additional-product-info__' + slug + '-content';

    var splitIdx = findMediaIndex(nodes);

    if (splitIdx > 0) {
      var split = document.createElement('div');
      split.className = 'additional-product-info__split-layout';

      var contentDiv = document.createElement('div');
      contentDiv.className = contentClass;
      appendNodes(contentDiv, nodes.slice(0, splitIdx));

      var imageDiv = document.createElement('div');
      imageDiv.className = 'additional-product-info__image';
      imageDiv.appendChild(unwrapMedia(nodes[splitIdx]));

      split.appendChild(contentDiv);
      split.appendChild(imageDiv);
      block.appendChild(split);

      var tail = nodes.slice(splitIdx + 1);
      if (tail.length) buildSequentialContent(block, tail, contentClass);

    } else if (splitIdx === 0) {
      buildSequentialContent(block, nodes, contentClass);

    } else {
      var contentWrap = document.createElement('div');
      contentWrap.className = contentClass;
      appendNodes(contentWrap, nodes);
      block.appendChild(contentWrap);
    }

    if (slug === 'ingredients') setupIngredientsToggle(block);

    return block;
  }

  function processDescription(root) {
    var source = root.querySelector('[data-pdp-description-source]');
    if (!source) return;
    if (source.getAttribute('data-pdp-processed') === 'true') return;

    var flatNodes = collectFlatNodes(source);
    if (!flatNodes.length) return;

    var sections = flatNodesToSections(flatNodes);
    if (!sections.length) return;

    var fragment = document.createDocumentFragment();
    sections.forEach(function (section) {
      if (!section.title && !section.nodes.length) return;
      fragment.appendChild(buildSection(section));
    });

    source.innerHTML = '';
    source.appendChild(fragment);
    source.setAttribute('data-pdp-processed', 'true');
  }

  function init() {
    document.querySelectorAll('[data-pdp-description]').forEach(processDescription);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

})();
